import { Reducer } from 'redux';

import { Actions } from 'store/actions/formRequest.action';
import {
  FORM_REQUEST_SET_PROVIDER,
  FORM_REQUEST_SET_PROVIDER_NAME,
  FORM_REQUEST_SET_PATIENT,
  FORM_REQUEST_SET_TYPE,
  FORM_REQUEST_SET_AUTHORIZATION,
  FORM_REQUEST_SET_PAYMENT,
  FORM_REQUEST_RESET,
  FORM_REQUEST_LOADING,
  FORM_REQUEST_SET_CARD_NUMBER,
  FORM_REQUEST_SET_CARD_TYPE,
  FORM_REQUEST_SET_REQUEST_ID,
  FORM_REQUEST_PATIENT_INFO,
  FORM_REQUEST_ERROR,
  FORM_REQUEST_SET_FORMS,
  FORM_REQUEST_SET_FILE_DATA,
  FORM_REQUEST_SET_AUTH_TYPE,
  FORM_REQUEST_SET_ESTIMATE_AUTH,
  FORM_REQUEST_SET_PAYMENT_METHOD,
  FORM_REQUEST_SET_REQUEST_DETAILS,
  FORM_REQUEST_SET_SHOW_PROMPT,
  FORM_REQUEST_SET_RID,
  FORM_REQUEST_SET_GOVT_ID,
  FORM_REQUEST_SET_PATIENT_FORM,
  FORM_REQUEST_SET_SUPPORTING_DOCS,
  FORM_REQUEST_DELETE_SUPPORTING_DOC
} from 'store/types/formRequest.type';
import { AuthFormValues, RequestDetail, GovtId, SupportingDocs, PatientForm } from 'types/requestItem';

import { RootReducerType } from './index';

export type FormRequestStepsType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export const FormRequestSteps: {
  Provider: FormRequestStepsType;
  Patient: FormRequestStepsType;
  Type: FormRequestStepsType;
  AuthSelection: FormRequestStepsType;
  Authorization: FormRequestStepsType;
  GovtId: FormRequestStepsType;
  SupportingDocs: FormRequestStepsType;
  RequestDetails: FormRequestStepsType;
  Payment: FormRequestStepsType;
  Confirm: FormRequestStepsType;
  RequestId: FormRequestStepsType;
} = Object.freeze({
  Provider: 1,
  Patient: 2,
  Type: 3,
  AuthSelection: 4,
  Authorization: 5,
  GovtId: 6,
  SupportingDocs: 7,
  RequestDetails: 8,
  Payment: 9,
  Confirm: 10,
  RequestId: 11,
});

export type State = {
  provider?: any;
  requestId?: string;
  cardNumber?: string;
  cardType?: string;
  patientId?: string | null;
  typeId?: string;
  auth?: AuthFormValues;
  govtId?: GovtId;
  patientForm?: PatientForm
  supportingDocs: SupportingDocs[]
  payment?: string;
  currentStep: FormRequestStepsType;
  patientInfo?: any;
  total: number;
  isLoading: boolean;
  error?: string;
  forms?: any;
  fileData?: any;
  authType?: any;
  estimateInitials?: string;
  paymentMethod?: string
  requestDetails?: RequestDetail
  showPrompt?: boolean
  rid?: number
};

const initialState: State = {
  currentStep: FormRequestSteps.Provider,
  supportingDocs: [],
  total: Object.getOwnPropertyNames(FormRequestSteps).length,
  isLoading: false,
};

const setState = (current: FormRequestStepsType, active: FormRequestStepsType) => {
  return current > active ? current : active;
};

export const formRequestReducer: Reducer<State, Actions> = (state = initialState, action) => {
  switch (action.type) {
    case FORM_REQUEST_SET_PROVIDER:
      return {
        ...state,
        provider: action.provider,
        currentStep: setState(state.currentStep, FormRequestSteps.Patient),
        showPrompt: true
      };
    case FORM_REQUEST_SET_PROVIDER_NAME:
      return {
        ...state,
        providerName: action.name,
        currentStep: setState(state.currentStep, FormRequestSteps.Patient),
      };
    case FORM_REQUEST_SET_REQUEST_DETAILS:
      return { ...state, requestDetails: action.payload, currentStep: action.stepChange ? setState(state.currentStep, FormRequestSteps.Payment) : state.currentStep }
    case FORM_REQUEST_SET_REQUEST_ID:
      return {
        ...state,
        requestId: action.preload,
        currentStep: setState(state.currentStep, FormRequestSteps.Confirm),
      };

    case FORM_REQUEST_SET_FORMS:
      return { ...state, forms: action.preload };


    case FORM_REQUEST_SET_PATIENT:
      return {
        ...state,
        patientId: action.id,
        patientInfo: null,
        auth: initialState.auth,
        currentStep: setState(state.currentStep, FormRequestSteps.Type),
      };
    case FORM_REQUEST_SET_CARD_NUMBER:
      return { ...state, cardNumber: action.preload, currentStep: setState(state.currentStep, FormRequestSteps.Type) };
    case FORM_REQUEST_SET_CARD_TYPE:
      return { ...state, cardType: action.preload, currentStep: setState(state.currentStep, FormRequestSteps.Type) };
    case FORM_REQUEST_SET_TYPE:
      return {
        ...state,
        typeId: action.id,
        fileData: action.fileData,
        currentStep: setState(state.currentStep, FormRequestSteps.Authorization),
      };
    case FORM_REQUEST_SET_FILE_DATA:
      return {
        ...state,
        typeId: undefined,
        fileData: action.preload,
        currentStep: setState(state.currentStep, FormRequestSteps.AuthSelection),
      };

    case FORM_REQUEST_SET_AUTH_TYPE:
      return { ...state, authType: action.preload, currentStep: setState(state.currentStep, FormRequestSteps.Authorization) };

    case FORM_REQUEST_SET_AUTHORIZATION:
      return { ...state, auth: {...state.auth, ...action.token}, currentStep: setState(state.currentStep, FormRequestSteps.GovtId) };
    case FORM_REQUEST_SET_GOVT_ID:
      return { ...state, govtId: { ...state.govtId, ...action.data }, currentStep: setState(state.currentStep, FormRequestSteps.SupportingDocs) };
    case FORM_REQUEST_SET_SUPPORTING_DOCS:
      return { ...state, supportingDocs: action.docs, currentStep: setState(state.currentStep, FormRequestSteps.RequestDetails) };
    case FORM_REQUEST_DELETE_SUPPORTING_DOC: {
      let index = state.supportingDocs.findIndex(doc => doc.fileId === action.fileId)
      if (index > -1) return { ...state, supportingDocs: [...state.supportingDocs.slice(0, index), { ...state.supportingDocs[index], deleted: true }, ...state.supportingDocs.slice(index + 1)] }
      else return state
    }

    case FORM_REQUEST_SET_PAYMENT_METHOD:
      return { ...state, paymentMethod: action.value, currentStep: setState(state.currentStep, FormRequestSteps.Confirm) };
    case FORM_REQUEST_SET_PAYMENT:
      return { ...state, payment: action.id, currentStep: setState(state.currentStep, FormRequestSteps.Confirm) };

    case FORM_REQUEST_PATIENT_INFO:
      return {
        ...state,
        patientInfo: action.preload,
        patientId: null,
        auth: initialState.auth,
        currentStep: setState(state.currentStep, FormRequestSteps.Type),
      };
    case FORM_REQUEST_SET_ESTIMATE_AUTH:
      return { ...state, estimateInitials: action.estimateInitials }

    case FORM_REQUEST_RESET:
      return initialState;
    case FORM_REQUEST_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
        currentStep: setState(state.currentStep, FormRequestSteps.RequestId),
      };
    case FORM_REQUEST_ERROR:
      return { ...state, isLoading: false, error: action.error };
    case FORM_REQUEST_SET_RID:
      return { ...state, rid: action.rid }
    case FORM_REQUEST_SET_SHOW_PROMPT:
      return { ...state, showPrompt: action.flag, requestDetails: action.data ? action.data : state.requestDetails }
    case FORM_REQUEST_SET_PATIENT_FORM:
        return { ...state, patientForm: { ...action.data } };
    default:
      return state;
  }
};

export const formRequestSelector = (state: RootReducerType) => state.formRequest;
export default formRequestReducer;
